import { Component, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { SettingsService, ProjectSettings } from '../../services/settings.service';
import { I18nService } from '../../services/i18n.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterModule],
  template: `
    <div class="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div class="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-8 w-full max-w-md z-10 border border-white/20">
        <div class="flex flex-col items-center mb-6">
          <img *ngIf="settings?.logoUrl" [src]="settings?.logoUrl" class="h-16 w-auto mb-4 object-contain" alt="Logo">
          <h1 class="text-3xl font-bold text-gray-900 text-center">{{ i18n.t('dashboard.title') }}</h1>
          <h2 class="text-lg font-medium text-gray-500 mt-2">{{ i18n.t('register.subtitle') }}</h2>
        </div>

        <form (ngSubmit)="onRegister()" class="space-y-4">
          <div>
            <label for="username" class="block text-sm font-medium text-gray-700 mb-1">{{ i18n.t('login.username') }}</label>
            <input
              type="text"
              id="username"
              [(ngModel)]="username"
              name="username"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              [placeholder]="i18n.t('login.username')"
              required
            />
          </div>

          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ i18n.t('register.email') }}</label>
            <input
              type="email"
              id="email"
              [(ngModel)]="email"
              name="email"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              [placeholder]="i18n.t('register.email')"
              required
            />
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">{{ i18n.t('login.password') }}</label>
            <input
              type="password"
              id="password"
              [(ngModel)]="password"
              name="password"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              [placeholder]="i18n.t('login.password')"
              required
            />
          </div>

          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">{{ i18n.t('register.confirmPassword') }}</label>
            <input
              type="password"
              id="confirmPassword"
              [(ngModel)]="confirmPassword"
              name="confirmPassword"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              [placeholder]="i18n.t('register.confirmPassword')"
              required
            />
          </div>

          <button
            type="submit"
            [disabled]="loading"
            class="btn-system-primary w-full py-3"
          >
            @if (loading) {
              {{ i18n.t('register.loading') }}
            } @else {
              {{ i18n.t('register.button') }}
            }
          </button>
        </form>

        <p class="mt-6 text-center text-gray-600">
          Already have an account?
          <a [routerLink]="['/login']" class="text-primary hover:text-primary/80 font-semibold cursor-pointer">Login</a>
        </p>
      </div>
    </div>
  `,
})
export class RegisterComponent {
  username = '';
  email = '';
  password = '';
  confirmPassword = '';
  loading = false;
  error = '';
  settings: ProjectSettings | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private settingsService: SettingsService,
    public i18n: I18nService
  ) {
    this.settingsService.settings$.subscribe(s => {
      this.settings = s;
      this.cdr.detectChanges();
    });
  }

  onRegister(): void {
    if (!this.username || !this.email || !this.password || !this.confirmPassword) {
      this.toastService.error('Please fill in all fields');
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.toastService.error('Passwords do not match');
      return;
    }

    if (this.password.length < 6) {
      this.toastService.error('Password must be at least 6 characters');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.authService.register(this.username, this.email, this.password, this.confirmPassword).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          this.toastService.success(this.i18n.t('toast.passwordSuccess'));
          this.router.navigate(['/dashboard']);
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          const errorMessage = err.error?.error || this.i18n.t('toast.passwordError');
          this.toastService.error(errorMessage);
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
    });
  }
}
